/**
 * ИМЯ ВКЛАДКИ И ВЕРСИИ ДАННЫХ (`s713` по контракту `s712`, `kontrakt-s712-aktualnost-arenda.md`).
 *
 * ⛔ ИМЯ ВКЛАДКИ живёт в памяти страницы, не в хранилище браузера: по нему сервер знает, в
 * каких окнах человека карточка ещё открыта (`s719`: свои окна не конфликтуют, но закрытие одного
 * не должно отпускать блокировку, пока карточка открыта в другом).
 *
 * ⛔ ВЕРСИЯ — отпечаток состояния лида на сервере. Экран запоминает её из последнего ответа
 * по ресурсу и возвращает с изменяющим ходом: если данные с тех пор изменил другой человек,
 * оплата или синхронизация, сервер ход не сохранит и скажет об этом.
 *
 * ⛔ СТАРУЮ ВЕРСИЮ НЕ ШЛЁМ. Версию знает только открытый экран (он перечитывает ресурс раз в
 * несколько секунд). Пока карточка открыта, её версия годна без срока — она и есть то, что видит
 * человек. Ход по ресурсу, чья карточка давно закрыта (перетаскивание на доске), запомненную
 * версию не шлёт: заведомо устаревшая дала бы отказ «данные изменились» на ровном месте. Такой
 * ход несёт версию плитки (её называет сам экран заголовком `X-Versiya`) либо версию на миг
 * взятия аренды (`vzyatNaHod`).
 */
export const VKLADKA: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `vk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const SVEZHEST_MS = 20_000;

const versii = new Map<string, { versiya: string; kogda: number }>();

/** Какой ресурс стоит за адресом хода: лид с перепиской — `inquiry:N`, запись — `booking:N`. */
export function resursAdresa(url: string): string | null {
  const put = url.split('?')[0];
  const lid = put.match(/(?:^|\/)obrashcheniya\/(\d+)(?:\/|$)/);
  if (lid) return `inquiry:${lid[1]}`;

  const zapis = put.match(/(?:^|\/)zapis\/(\d+)(?:\/|$)/);
  if (zapis) return `booking:${zapis[1]}`;

  // Бот (`s721`): двери правки `operations/robot/boty/N/…` и карточка `operations/robot/ekran/boty/N`.
  const bot = put.match(/(?:^|\/)operations\/robot\/(?:ekran\/)?boty\/(\d+)(?:\/|$)/);
  if (bot) return `bot:${bot[1]}`;

  // Двери аренды называют вид сами — любой, в том числе добавленный позже.
  const pryamo = put.match(/(?:^|\/)aktualnost\/(?:resurs|arenda)\/([a-z_]+)\/(\d+)(?:\/|$)/);
  if (pryamo) return `${pryamo[1]}:${pryamo[2]}`;

  return null;
}

export function zapomnitVersiyu(resurs: string, versiya: unknown): void {
  if (typeof versiya === 'string' && versiya !== '') versii.set(resurs, { versiya, kogda: Date.now() });
}

/** Версия для изменяющего хода — только свежая (экран ресурса открыт и перечитывается). */
export function versiyaDlyaHoda(resurs: string, kartochkaOtkryta = false): string | null {
  const z = versii.get(resurs);
  if (z === undefined) return null;

  return kartochkaOtkryta || Date.now() - z.kogda <= SVEZHEST_MS ? z.versiya : null;
}

/** Последняя известная версия ресурса — для показа и для хука карточки. */
export function versiyaResursa(resurs: string): string | null {
  return versii.get(resurs)?.versiya ?? null;
}

/**
 * Адреса под стражем, которые ресурс не правят (сервер: `config('aktualnost.bez_strazha')`):
 * отметка «прочитано», расшифровка голосового, начало переписки из записи; копия, дубль и запуск
 * бота. Аренда им не нужна.
 */
export function hodBezArendy(url: string): boolean {
  const put = url.split('?')[0];

  return /\/obrashcheniya\/\d+\/prochitano$/.test(put)
    || /\/obrashcheniya\/\d+\/rasshifrovat\/[^/]+$/.test(put)
    || /\/zapis\/\d+\/perepiska$/.test(put)
    // Бот: копия, дубль и ручной запуск самого бота не правят (на сервере стоят вне стража).
    || /\/operations\/robot\/boty\/\d+\/(?:kopiya|dublirovat|zapusk)$/.test(put)
    // «Сколько прошли бы вчера» — POST только из-за тела запроса, бота не правит (`s721`).
    || /\/operations\/robot\/ekran\/boty\/\d+\/vchera(?:-usloviya)?$/.test(put)
    || /(?:^|\/)aktualnost\//.test(put);
}

/** Для проверок. */
export function sbrositVersii(): void {
  versii.clear();
}
